// New-device side of linking: opened from the QR code `#/link/<secret>`.

import { getSpace, requestPersistence, setSpace } from '../space/current';
import { deviceCode, joinByLink } from '../space/link';
import { Space } from '../space/space';
import { h } from './dom';
import { deviceName, iceServers, wsUrl } from './common';
import type { View } from './view';

export function linkView(root: HTMLElement, secret: string): View {
  let alive = true;
  let close: (() => void) | null = null;

  const show = (...nodes: Node[]) => alive && root.replaceChildren(h('h1', { text: 'Lier cet appareil' }), ...nodes);

  let stall: ReturnType<typeof setTimeout> | undefined;

  async function join() {
    try {
      const result = await joinByLink(secret, deviceName(), wsUrl, await iceServers(), (status, device) => {
        clearTimeout(stall);
        if (status === 'connecting') {
          show(h('p', { class: 'dim', text: 'Connexion à ton autre appareil…' }));
          // Still trying in the background; this only explains what usually blocks.
          stall = setTimeout(
            () =>
              show(
                h('p', { class: 'dim', text: 'Connexion à ton autre appareil…' }),
                h('p', { class: 'warn', text: 'Ton autre appareil ne répond pas. Vérifie que l’app y est ouverte au premier plan sur l’écran « Lier un appareil », écran allumé. Le QR code expire après 10 minutes : s’il est plus ancien, génères-en un nouveau et ouvre ce nouveau lien ici.' }),
                h('a', { class: 'btn small', href: '#/notes', text: 'Annuler' }),
              ),
            20_000,
          );
        } else {
          show(
            h('p', { text: 'Confirme sur ton autre appareil. Il doit afficher ce code :' }),
            h('p', { class: 'code', text: deviceCode(device.id) }),
          );
        }
      });
      close = result.close;
      const space = await Space.join(result.device, result.secrets);
      requestPersistence();
      setSpace(space);
      show(h('p', { class: 'dim', text: 'Accepté. Synchronisation de tes notes…' }));
      await space.whenMember();
      result.close();
      if (alive) location.hash = '#/notes';
    } catch (e) {
      const msg = (e as Error).message;
      const text =
        msg === 'declined'
          ? 'L’autre appareil a refusé.'
          : msg.startsWith('leftover')
            ? 'Cet appareil contient encore des notes d’un ancien espace. Va dans Notes pour les exporter ou les effacer, puis recommence la liaison.'
            : `Liaison impossible : ${msg}`;
      show(h('p', { class: 'bad', text }), h('a', { class: 'btn', href: '#/notes', text: 'Notes' }));
    }
  }

  void getSpace().then((existing) => {
    if (existing) {
      show(
        h('p', { text: 'Cet appareil a déjà un espace. Pour le lier à un autre, quitte d’abord l’espace actuel (Appareils → Quitter l’espace).' }),
        h('a', { class: 'btn', href: '#/devices', text: 'Appareils' }),
      );
      return;
    }
    show(
      h('p', { class: 'dim', text: 'Cet appareil va rejoindre ton espace : tes notes y seront synchronisées.' }),
      h('button', { class: 'btn primary', text: 'Continuer', onclick: () => void join() }),
    );
  });

  return {
    destroy() {
      alive = false;
      clearTimeout(stall);
      close?.();
    },
  };
}
